/**
 * Homepage hero + deals carousel (sections/hero.liquid, "Show deals slide").
 *
 * Two slides: the hero (primary, shown on load) and the deals card to its
 * right. Navigation is by the prev/next buttons over the peeking sliver
 * (desktop), the two dots under the hero (smaller screens), touch swipe, a
 * horizontal trackpad swipe, or the arrow keys while focus is in the
 * carousel. Never auto-advances — the hero holds the search
 * bar and shouldn't move under someone about to type.
 *
 * Discoverability: the first time the carousel is mostly in view each
 * session, the track nudges left to show the deals card and settles back
 * (skipped for reduced motion, or once the visitor has navigated).
 */
(function () {
  if (customElements.get('hero-carousel')) return;

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  var DRAG_START_PX = 8;

  function storageGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      /* private mode etc. — the nudge just plays again next load */
    }
  }

  class HeroCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.hc__viewport');
      this.track = this.querySelector('.hc__track');
      if (!this.viewport || !this.track) return;

      this.slides = Array.prototype.slice.call(this.track.children);
      this.controls = Array.prototype.slice.call(this.querySelectorAll('[data-hc-go]'));
      this.index = 0;
      this.interacted = false;

      this.onControlClick = this.onControlClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onWheel = this.onWheel.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.controls.forEach(function (el) {
        el.addEventListener('click', this.onControlClick);
      }, this);
      this.addEventListener('keydown', this.onKeyDown);
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('click', this.onClickCapture, true);
      this.viewport.addEventListener('wheel', this.onWheel, { passive: true });
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.goTo(0, { silent: true });
      this.setupNudge();
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.observer) this.observer.disconnect();
      clearTimeout(this.nudgeTimer);
    }

    goTo(i, opts) {
      var next = Math.max(0, Math.min(this.slides.length - 1, i));
      this.index = next;
      this.style.setProperty('--hc-index', String(next));
      this.dataset.index = String(next);

      this.slides.forEach(function (slide, n) {
        var active = n === next;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
      });

      // Only buttons pointing at the hidden slide are reachable; dots also
      // mark the shown one.
      this.controls.forEach(function (el) {
        var target = Number(el.getAttribute('data-hc-go'));
        el.tabIndex = target === next ? -1 : 0;
        if (el.classList.contains('hc__dot')) {
          el.setAttribute('aria-current', target === next ? 'true' : 'false');
        }
      });

      if (!(opts && opts.silent)) {
        this.markInteracted();
      }
    }

    markInteracted() {
      if (this.interacted) return;
      this.interacted = true;
      this.cancelNudge();
      storageSet(this.dataset.nudgeKey, '1');
    }

    onControlClick(event) {
      var target = Number(event.currentTarget.getAttribute('data-hc-go'));
      this.goTo(target);
      // The pressed button hides once its slide is shown; hand keyboard focus
      // to the one pointing back so it stays in the carousel.
      if (event.detail === 0) this.focusControlFor(target);
    }

    focusControlFor(shown) {
      // The visible control at this width: peek on desktop, dot below.
      var back = this.controls.find(function (el) {
        return Number(el.getAttribute('data-hc-go')) !== shown && el.offsetParent !== null;
      });
      if (back) back.focus({ preventScroll: true });
    }

    onKeyDown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      var t = event.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      event.preventDefault();
      var next = this.index + (event.key === 'ArrowRight' ? 1 : -1);
      if (next === this.index || next < 0 || next >= this.slides.length) return;
      var fromControl = this.controls.indexOf(t) !== -1;
      this.goTo(next);
      if (fromControl) this.focusControlFor(next);
    }

    /* ---------- Touch swipe ---------- */

    onPointerDown(event) {
      // Mouse users get the prev/next buttons; dragging a page-width
      // banner with a mouse fights text selection and link clicks.
      if (event.pointerType === 'mouse' || !event.isPrimary) return;
      if (event.target.closest('input, textarea, select')) return;
      this.drag = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        dx: 0,
        active: false,
        width: this.viewport.clientWidth,
      };
      this.viewport.addEventListener('pointermove', this.onPointerMove);
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      var d = this.drag;
      if (!d || event.pointerId !== d.id) return;
      var dx = event.clientX - d.x;
      var dy = event.clientY - d.y;

      if (!d.active) {
        if (Math.abs(dy) > DRAG_START_PX && Math.abs(dy) > Math.abs(dx)) {
          this.endDrag(); // vertical scroll — let the page have it
          return;
        }
        if (Math.abs(dx) < DRAG_START_PX) return;
        d.active = true;
        this.cancelNudge();
        this.classList.add('is-dragging');
        try {
          this.viewport.setPointerCapture(d.id);
        } catch (e) {}
      }

      // Rubber-band past either end.
      var atEdge = (this.index === 0 && dx > 0) || (this.index === this.slides.length - 1 && dx < 0);
      d.dx = atEdge ? dx * 0.3 : dx;
      this.track.style.translate = d.dx + 'px 0';
    }

    onPointerUp(event) {
      var d = this.drag;
      if (!d || event.pointerId !== d.id) return;
      var wasActive = d.active;
      var dx = d.dx;
      var threshold = Math.min(80, d.width * 0.18);
      this.endDrag();
      if (!wasActive) return;

      this.suppressClick = true;
      setTimeout(() => {
        this.suppressClick = false;
      }, 0);

      if (dx < -threshold) this.goTo(this.index + 1);
      else if (dx > threshold) this.goTo(this.index - 1);
    }

    endDrag() {
      this.viewport.removeEventListener('pointermove', this.onPointerMove);
      this.viewport.removeEventListener('pointerup', this.onPointerUp);
      this.viewport.removeEventListener('pointercancel', this.onPointerUp);
      this.classList.remove('is-dragging');
      this.track.style.translate = '';
      this.drag = null;
    }

    onClickCapture(event) {
      if (this.suppressClick) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    /* ---------- Trackpad horizontal swipe ---------- */

    onWheel(event) {
      if (Math.abs(event.deltaX) < 24 || Math.abs(event.deltaX) < Math.abs(event.deltaY) * 1.5) return;
      if (this.wheelLocked) return;
      var next = this.index + (event.deltaX > 0 ? 1 : -1);
      if (next < 0 || next >= this.slides.length) return;
      this.wheelLocked = true;
      setTimeout(() => {
        this.wheelLocked = false;
      }, 700);
      this.goTo(next);
    }

    /* ---------- Theme editor ---------- */

    onBlockSelect(event) {
      var slide = this.slides.find(function (s) {
        return s.contains(event.target);
      });
      if (slide) this.goTo(this.slides.indexOf(slide), { silent: true });
    }

    /* ---------- One-time nudge ---------- */

    setupNudge() {
      if (REDUCED_MOTION.matches || (window.Shopify && window.Shopify.designMode)) return;
      if (storageGet(this.dataset.nudgeKey)) return;
      if (!('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          var entry = entries[0];
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.observer.disconnect();
            this.nudgeTimer = setTimeout(() => this.playNudge(), 1400);
          }
        },
        { threshold: [0.6] }
      );
      this.observer.observe(this.viewport);
    }

    playNudge() {
      if (this.interacted || this.index !== 0 || document.hidden) return;
      // Don't move the hero while someone is using its search.
      if (this.slides[0].contains(document.activeElement) && document.activeElement !== document.body) return;
      storageSet(this.dataset.nudgeKey, '1');
      this.classList.add('is-nudging');
      this.track.addEventListener(
        'animationend',
        () => {
          this.classList.remove('is-nudging');
        },
        { once: true }
      );
    }

    cancelNudge() {
      clearTimeout(this.nudgeTimer);
      if (this.observer) this.observer.disconnect();
      this.classList.remove('is-nudging');
    }
  }

  customElements.define('hero-carousel', HeroCarousel);
})();
